import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { getRegionNames } from "../../../actions/regions/RegionActions";
import {
  getZones,
  clearZones,
  createZone,
  updateZone,
  deleteZone,
  openCreateAndUpdateZoneForm,
} from "../../actions/campaign-planning/ZonesAndSubZonesActions";
import { openConfirmationForm } from "../../../actions/ConfirmationFormActions";

// Constants and Utils
import { FormDataTargets } from "../../../constants/GeneralConstants";

// Components
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import TableHeaders from "../../../components/table/TableHeaders";
import Spinner from "../../../components/spinner/Spinner";
import ConfirmationForm from "../../../components/confirmation-form/ConfirmationForm";
import SingleCitySelectionForm from "../../../mavin/components/single-city-selection-form/SingleCitySelectionForm";
import { LoadingData } from "../../../pages/org/OrgUtils";
import CityConfigSidebar from "./CityConfigSidebar";
import { CitySelectSection } from "./CityConfigUtils";
import CreateAndUpdateZoneForm from "./CreateAndUpdateZoneForm";

// Page Constants
const tableHeaders = [
  {
    title: {
      displayName: "Name",
      className: "col-2 align-middle",
    },
  },
  {
    title: {
      displayName: "State",
      className: "col-2 align-middle",
    },
  },
  {
    title: {
      displayName: "District",
      className: "col-2 align-middle",
    },
  },
  {
    title: {
      displayName: "bbox",
      className: "col-3 align-middle",
    },
    subTitle: {
      displayName: "(minLng, minLat, maxLng, maxLat)",
    },
  },
  {
    title: {
      displayName: "Actions",
      className: "col-3 align-middle text-center",
    },
  },
];

// Page Components
function CreateZoneButton({ selectedCityId }) {
  const dispatch = useDispatch();

  // Selector
  const createZoneLoading = useSelector(
    (state) => state.zonesAndSubZones.createZoneLoading
  );

  // hide button when no city selected
  if (!selectedCityId) {
    return null;
  }

  return (
    <button
      className="btn btn-primary shadow-none"
      disabled={createZoneLoading}
      data-toggle="modal"
      data-target={`#${FormDataTargets.createAndUpdateZoneForm}`}
      onClick={() => dispatch(openCreateAndUpdateZoneForm(false))}
    >
      Create Zone{" "}
      {createZoneLoading && <Spinner className="spinner-border-sm" />}
    </button>
  );
}

// Zones Table Actions
function ZonesActionsCell({ zone = {} }) {
  const dispatch = useDispatch();

  // Selector
  const updateZoneLoadingMap = useSelector(
    (state) => state.zonesAndSubZones.updateZoneLoadingMap
  );
  const deleteZoneLoadingMap = useSelector(
    (state) => state.zonesAndSubZones.deleteZoneLoadingMap
  );

  // zoneId and zoneName
  const { id: zoneId, name: zoneName } = zone || {};

  // button data
  const actionsData = [
    {
      label: "Update",
      onClick: () => dispatch(openCreateAndUpdateZoneForm(true, zone)),
      icon: "fa-pen",
      loading: updateZoneLoadingMap[zoneId],
      dataToggle: "modal",
      dataTarget: `#${FormDataTargets.createAndUpdateZoneForm}`,
    },
    {
      label: "Delete",
      onClick: () => dispatch(openConfirmationForm({ zoneId, zoneName })),
      icon: "fa-trash",
      className: "ml-3",
      loading: deleteZoneLoadingMap[zoneId],
      dataToggle: "modal",
      dataTarget: `#${FormDataTargets.confirmationForm}`,
    },
  ];

  return (
    <td className="text-center">
      {actionsData.map((data, i) => {
        const {
          label,
          onClick,
          icon,
          className = "",
          loading = false,
          dataToggle,
          dataTarget,
        } = data;
        return (
          <button
            key={i}
            className={`btn btn-link shadow-none p-0 ${className}`}
            onClick={onClick}
            disabled={loading}
            data-toggle={dataToggle}
            data-target={dataTarget}
          >
            {loading ? (
              <Spinner className="spinner-border-sm mx-2" />
            ) : (
              <i className={`fa ${icon} text-primary mx-2`} />
            )}
            {label}
          </button>
        );
      })}
    </td>
  );
}

// Zones Table Section
function ZonesTableSection({ selectedCityId }) {
  // Zones
  const zones = useSelector((state) => state.zonesAndSubZones.zones);
  const zonesLoading = useSelector(
    (state) => state.zonesAndSubZones.zonesLoading
  );

  // if city not selected
  if (!selectedCityId) {
    return null;
  }

  // zones loading
  if (zonesLoading) {
    return <LoadingData className="p-1 mt-3" dataType={"Zones"} />;
  }

  // if zones not exist
  const isZonesPresent = zones.length > 0;

  return (
    <table className="table mt-3">
      <TableHeaders tableHeaders={tableHeaders} />

      <tbody>
        {!isZonesPresent && (
          <tr className="text-center">
            <td colSpan={4} className="font-italic text-muted">
              No Zones Available
            </td>
          </tr>
        )}

        {isZonesPresent &&
          zones.map((zone) => {
            const { id, name, bbox, district, state } = zone;
            const [minLng, minLat, maxLng, maxLat] = bbox.split(",");
            const min = `${minLng},${minLat}`;
            const max = `${maxLng},${maxLat}`;
            return (
              <tr key={id}>
                <td>{name}</td>
                <td>{state ? state : "--"}</td>
                <td>{district ? district : "--"}</td>
                <td>
                  <span className="d-block">{min},</span>
                  <span>{max}</span>
                </td>
                <ZonesActionsCell zone={zone} />
              </tr>
            );
          })}
      </tbody>
    </table>
  );
}

/**
 * Zones city config Page
 */
export default function ZonesCityConfigPage() {
  const dispatch = useDispatch();

  // State
  const [selectedCityId, setSelectedCityId] = useState("");

  // regions name list
  const regionsName = useSelector((state) => state.region.regionsName) || [];
  const regionNamesLoading = useSelector((state) => state.region.namesLoading);

  // single City-Selection-form
  const openSingleCitySelectionForm = useSelector(
    (state) => state.singleCitySelectionModal.openModal
  );

  // create update Zone
  const openCreateAndUpdateZoneModal = useSelector(
    (state) => state.zonesAndSubZones.openCreateAndUpdateZoneModal
  );

  // Confirmation Form :: [true/false and dataObject]
  const openConfirmationForm = useSelector(
    (state) => state.confirmationModal.openModal
  );
  const confirmProcessData = useSelector(
    (state) => state.confirmationModal.processDataObject
  );
  const { zoneId: zoneIdToDelete, zoneName: zoneNameToDelete } =
    confirmProcessData || {};
  const confirmDeleteBodyText = `Are you sure you want to delete "${zoneNameToDelete}" zone?`;

  // get region names
  useEffect(() => {
    dispatch(getRegionNames());
  }, [dispatch]);

  // Functions
  function onSubmitCitySelectionForm(cityId) {
    setSelectedCityId(cityId);

    // get zones
    dispatch(clearZones());
    dispatch(getZones(cityId));
  }

  function onSubmitCreateUpdateZoneForm(isUpdate, zoneBean, zoneId) {
    // api calls
    if (isUpdate) {
      dispatch(updateZone(zoneId, zoneBean, selectedCityId));
    } else {
      dispatch(createZone(zoneBean, selectedCityId));
    }
  }

  function onSubmitDeleteZoneForm() {
    if (zoneIdToDelete) {
      dispatch(deleteZone(zoneIdToDelete, selectedCityId));
    }
  }

  return (
    <>
      <CityConfigSidebar activePageUrl={"/city-config/zones"} />

      <div className="content-wrapper">
        <PageHeader title={"Zones"} shadow={true} />

        <div className="page-content">
          {/* regions list loading */}
          {regionNamesLoading && <LoadingData className="p-1" />}
          {!regionNamesLoading && (
            <>
              <div className="d-flex align-items-center justify-content-between border-bottom pb-3">
                <CitySelectSection
                  regionsName={regionsName}
                  selectedCityId={selectedCityId}
                />
                <CreateZoneButton selectedCityId={selectedCityId} />
              </div>

              {/* Zones Table Section */}
              <ZonesTableSection selectedCityId={selectedCityId} />
            </>
          )}
        </div>
      </div>

      {/* MODALS */}
      {openSingleCitySelectionForm && (
        <SingleCitySelectionForm
          regionsName={regionsName}
          onSubmit={onSubmitCitySelectionForm}
          selectedCityId={selectedCityId}
        />
      )}

      {/* create update zone */}
      {openCreateAndUpdateZoneModal && (
        <CreateAndUpdateZoneForm onSubmit={onSubmitCreateUpdateZoneForm} />
      )}

      {/* delete confirmation */}
      {openConfirmationForm && (
        <ConfirmationForm
          bodyText={confirmDeleteBodyText}
          onClick={onSubmitDeleteZoneForm}
        />
      )}
    </>
  );
}
